import { ReactNode, CSSProperties } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

type AnimVariant = 'fade-up' | 'fade-in' | 'fade-left' | 'fade-right' | 'zoom-in';

interface AnimateOnScrollProps {
  children: ReactNode;
  variant?: AnimVariant;
  delay?: number;
  duration?: number;
  className?: string;
  threshold?: number;
}

const baseStyle: CSSProperties = {
  transition: 'opacity 0.7s cubic-bezier(0.22,1,0.36,1), transform 0.7s cubic-bezier(0.22,1,0.36,1)',
};

function getHiddenStyle(variant: AnimVariant): CSSProperties {
  switch (variant) {
    case 'fade-up':    return { opacity: 0, transform: 'translateY(36px)' };
    case 'fade-left':  return { opacity: 0, transform: 'translateX(-36px)' };
    case 'fade-right': return { opacity: 0, transform: 'translateX(36px)' };
    case 'zoom-in':    return { opacity: 0, transform: 'scale(0.94)' };
    default:           return { opacity: 0 };
  }
}

const visibleStyle: CSSProperties = { opacity: 1, transform: 'none' };

export default function AnimateOnScroll({
  children,
  variant = 'fade-up',
  delay = 0,
  duration = 700,
  className = '',
  threshold = 0.12,
}: AnimateOnScrollProps) {
  const { ref, visible } = useScrollAnimation({ threshold });

  const style: CSSProperties = {
    ...baseStyle,
    transitionDuration: `${duration}ms`,
    transitionDelay: visible ? `${delay}ms` : '0ms',
    ...(visible ? visibleStyle : getHiddenStyle(variant)),
  };

  return (
    <div ref={ref} style={style} className={className}>
      {children}
    </div>
  );
}
